/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import FacilityService from "../services/FacilityService";
import { If, PositiveNumInput } from "../utils/Controls";
import { AntdPaging, AntdSelect, AntdTag, MultiChechBox } from "../utils/Antd";
import AssetService from '../services/AssetService';
import util from "../utils/util";
import plus from "../pages/assets/icons/plus.svg"
import edit from "../pages/assets/icons/pen-to-square-solid.svg"
import xmark from "../pages/assets/icons/circle-xmark-solid.svg"
import search from "../pages/assets/icons/magnifying-glass-solid.svg"
import './analytics.css';
import {
    Input,
    Button,
    message,
    Modal,
    Checkbox,
    Tag,
    Card,
    Switch,
    Row,
    Col,
    Statistic
} from 'antd';
import {
    ExclamationCircleOutlined,
    BarChartOutlined,
    EnvironmentOutlined,

    BankOutlined
} from '@ant-design/icons';

const { confirm } = Modal;
// Analytics Panel Component
const AnalyticsPanel = ({ facilities, isVisible, loading, totalRecords, onFilterClick, activeFilters }) => {
    const [analytics, setAnalytics] = useState({
        byLevel: {},
        byRegion: {},
        byAssetCategory: {},
        totalFacilities: 0,
        activeFacilities: 0
    });

    useEffect(() => {
        if (facilities && facilities.length > 0) {
            calculateAnalytics();
        } else if (!loading) {
            // Reset analytics when no data
            setAnalytics({
                byLevel: {},
                byRegion: {},
                byAssetCategory: {},
                totalFacilities: 0,
                activeFacilities: 0
            });
        }
    // Modify your existing useEffect at the bottom to include Google Maps API loading

    }, [facilities, loading]);

    const calculateAnalytics = () => {
        const levelCounts = {};
        const regionCounts = {};
        const assetCategoryCounts = {};
        let totalFacilities = facilities.length;
        let activeFacilities = 0;

        facilities.forEach(facility => {
            // Count by level
            const level = facility.site_type || 'Not Specified';
            levelCounts[level] = (levelCounts[level] || 0) + 1;

            // Count by region
            const region = facility.located_region || 'Not Specified';
            regionCounts[region] = (regionCounts[region] || 0) + 1;

            // Count by asset categories (improved to show actual category names)
            if (facility.asset_cat_ids) {
                const categories = facility.asset_cat_ids.split(',');
                categories.forEach(catId => {
                    // You might want to map this to actual category names if available
                    const catName = `Category ${catId.trim()}`;
                    assetCategoryCounts[catName] = (assetCategoryCounts[catName] || 0) + 1;
                });
            }

            // Count active facilities
            if (facility.status) {
                activeFacilities++;
            }
        });

        setAnalytics({
            byLevel: levelCounts,
            byRegion: regionCounts,
            byAssetCategory: assetCategoryCounts,
            totalFacilities,
            activeFacilities
        });
    };

    const renderDistributionCards = (data, title, icon, color = '#1890ff', filterType) => {
        const sortedData = Object.entries(data).sort((a, b) => b[1] - a[1]);
        
        if (sortedData.length === 0) {
            return (
                <Card 
                    title={
                        <span style={{ color: 'white' }}>
                            {icon} {title}
                        </span>
                    } 
                    size="small"
                    className="analytics-card"
                    headStyle={{ 
                        background: `linear-gradient(90deg, ${color} 0%, ${color}dd 100%)`,
                        borderRadius: '6px 6px 0 0'
                    }}
                >
                    <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                        No data available
                    </div>
                </Card>
            );
        }
        
        return (
            <Card 
                title={
                    <span style={{ color: 'white' }}>
                        {icon} {title}
                        <span style={{ fontSize: '12px', marginLeft: '8px', opacity: 0.8 }}>
                            (Click to filter)
                        </span>
                    </span>
                } 
                size="small"
                className="analytics-card"
                loading={loading}
                headStyle={{ 
                    background: `linear-gradient(90deg, ${color} 0%, ${color}dd 100%)`,
                    borderRadius: '6px 6px 0 0'
                }}
            >
                <Row gutter={[16, 16]}>
                    {sortedData.map(([key, value]) => {
                        const isActive = activeFilters[filterType] === key;
                        return (
                            <Col xs={24} sm={12} md={8} lg={6} key={key}>
                                <Card 
                                    size="small" 
                                    className={`stat-card clickable-stat-card ${isActive ? 'active-filter' : ''}`}
                                    onClick={() => onFilterClick(filterType, key)}
                                    style={{
                                        cursor: 'pointer',
                                        border: isActive ? `2px solid ${color}` : '1px solid #f0f0f0',
                                        background: isActive ? `${color}08` : 'white'
                                    }}
                                >
                                    <Statistic
                                        title={key}
                                        value={value}
                                        suffix={analytics.totalFacilities > 0 ? `(${((value / analytics.totalFacilities) * 100).toFixed(1)}%)` : ''}
                                        valueStyle={{ 
                                            fontSize: '16px', 
                                            color: isActive ? color : color,
                                            fontWeight: isActive ? '700' : '600'
                                        }}
                                    />
                                    {isActive && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '4px',
                                            right: '4px',
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            background: color
                                        }} />
                                    )}
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            </Card>
        );
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className="analytics-panel" style={{
            marginBottom: '20px',
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e8e8e8'
        }}>
            {/* Summary Statistics */}
            <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }} loading={loading}>
                        <Statistic
                            title="Total Facilities"
                            value={analytics.totalFacilities}
                            prefix={<BankOutlined />}
                            valueStyle={{ color: '#1890ff', fontSize: '20px', fontWeight: '700' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }} loading={loading}>
                        <Statistic
                            title="Active Facilities"
                            value={analytics.activeFacilities}
                            suffix={analytics.totalFacilities > 0 ? `/ ${analytics.totalFacilities}` : ''}
                            valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: '700' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }} loading={loading}>
                        <Statistic
                            title="Active Rate"
                            value={analytics.totalFacilities > 0 ? ((analytics.activeFacilities / analytics.totalFacilities) * 100).toFixed(1) : 0}
                            suffix="%"
                            valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: '700' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small" style={{ textAlign: 'center' }} loading={loading}>
                        <Statistic
                            title="Inactive Facilities"
                            value={analytics.totalFacilities - analytics.activeFacilities}
                            valueStyle={{ color: '#ff4d4f', fontSize: '20px', fontWeight: '700' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Distribution Charts */}
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    {renderDistributionCards(
                        analytics.byLevel, 
                        "Distribution by Level", 
                        <BarChartOutlined />,
                        '#1890ff',
                        'level'
                    )}
                </Col>
                <Col xs={24} lg={12}>
                    {renderDistributionCards(
                        analytics.byRegion, 
                        "Distribution by Region", 
                        <EnvironmentOutlined />,
                        '#52c41a',
                        'region'
                    )}
                </Col>
            </Row>

            {loading && (
                <div style={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    background: 'rgba(255, 255, 255, 0.8)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    borderRadius: '8px'
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div className="loading-spinner" style={{ 
                            border: '3px solid #f3f3f3',
                            borderTop: '3px solid #1890ff',
                            borderRadius: '50%',
                            width: '30px',
                            height: '30px',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 10px'
                        }}></div>
                        <div style={{ color: '#666', fontSize: '14px' }}>Loading analytics...</div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Add this component before your main Facilities component
const FacilitiesMap = ({ facilities, isVisible, onClose, activeFilters }) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef([]);
    const infoWindowRef = useRef(null);

    // Eswatini bounds for initial map view
    const eswatiniBounds = {
        north: -25.7,
        south: -27.5,
        west: 30.8,
        east: 32.2
    };

    // Default center of Eswatini (approximate)
    const eswatiniCenter = {
        lat: -26.5,
        lng: 31.5
    };

    useEffect(() => {
        if (isVisible && window.google && mapRef.current && !mapInstanceRef.current) {
            initializeMap();
        }
    }, [isVisible]);

    useEffect(() => {
        if (mapInstanceRef.current && facilities) {
            updateMarkers();
        }
    }, [facilities, activeFilters]);

    const initializeMap = () => {
        const map = new window.google.maps.Map(mapRef.current, {
            zoom: 8,
            center: eswatiniCenter,
            restriction: {
                latLngBounds: eswatiniBounds,
                strictBounds: false,
            },
            mapTypeId: 'roadmap',
            styles: [
                {
                    featureType: 'administrative.country',
                    elementType: 'geometry.stroke',
                    stylers: [{ color: '#4285f4' }, { weight: 2 }]
                }
            ]
        });

        mapInstanceRef.current = map;
        infoWindowRef.current = new window.google.maps.InfoWindow();
        updateMarkers();
    };

 const clearMarkers = () => {
        markersRef.current.forEach(marker => {
            if (marker.setMap) {
                marker.setMap(null);
            }
            if (marker.close) {
                marker.close();
            }
        });
        markersRef.current = [];
    };
    const getMarkerColor = (facility) => {
        if (!facility.status) return '#ff4d4f'; // Red for inactive
        
        // Color by region
        const regionColors = {
            'Hhohho': '#1890ff',
            'Lubombo': '#52c41a', 
            'Manzini': '#fa8c16',
            'Shiselweni': '#722ed1'
        };
        
        return regionColors[facility.located_region] || '#666666';
    };

    const getMarkerIcon = (facility) => {
        const color = getMarkerColor(facility);
        return {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: color,
            fillOpacity: 0.8,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: facility.status ? 8 : 6
        };
    };

    const updateMarkers = () => {
        if (!mapInstanceRef.current || !facilities) return;

        clearMarkers();

        const validFacilities = facilities.filter(facility => 
            facility.latitude && facility.longitude && 
            !isNaN(parseFloat(facility.latitude)) && 
            !isNaN(parseFloat(facility.longitude))
        );

        if (validFacilities.length === 0) return;

        const bounds = new window.google.maps.LatLngBounds();

        validFacilities.forEach(facility => {
            const position = {
                lat: parseFloat(facility.latitude),
                lng: parseFloat(facility.longitude)
            };

            const marker = new window.google.maps.Marker({
                position,
                map: mapInstanceRef.current,
                title: facility.business_name,
                icon: getMarkerIcon(facility)
            });

            // Create info window content
            const infoContent = `
                <div style="max-width: 300px; padding: 10px;">
                    <h3 style="margin: 0 0 10px 0; color: #1890ff;">${facility.business_name}</h3>
                    <div style="margin-bottom: 8px;">
                        <strong>RC:</strong> ${facility.site_code || 'N/A'}<br>
                        <strong>Facility Code:</strong> ${facility.fac_code || 'N/A'}<br>
                        <strong>Level:</strong> ${facility.site_type || 'N/A'}<br>
                        <strong>Region:</strong> ${facility.located_region || 'N/A'}<br>
                        <strong>Owner:</strong> ${facility.facility_owner || 'N/A'}
                    </div>
                    <div style="margin-bottom: 8px;">
                        <strong>Address:</strong> ${facility.address || 'N/A'}
                    </div>
                    <div style="margin-bottom: 8px;">
                        <strong>Admin:</strong> ${facility.name}<br>
                        <strong>Email:</strong> ${facility.email}<br>
                        <strong>Phone:</strong> ${facility.phone1 || 'N/A'}
                    </div>
                    <div>
                        <span style="padding: 2px 6px; border-radius: 4px; font-size: 12px; color: white; background-color: ${facility.status ? '#52c41a' : '#ff4d4f'};">
                            ${facility.status ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                </div>
            `;

            marker.addListener('click', () => {
                infoWindowRef.current.setContent(infoContent);
                infoWindowRef.current.open(mapInstanceRef.current, marker);
            });

            markersRef.current.push(marker);
            bounds.extend(position);
        });

        // Fit map to show all markers
        if (validFacilities.length > 1) {
            mapInstanceRef.current.fitBounds(bounds);
            
            // Ensure minimum zoom level for small areas
            const listener = window.google.maps.event.addListener(mapInstanceRef.current, 'bounds_changed', () => {
                if (mapInstanceRef.current.getZoom() > 12) {
                    mapInstanceRef.current.setZoom(12);
                }
                window.google.maps.event.removeListener(listener);
            });
        } else if (validFacilities.length === 1) {
            mapInstanceRef.current.setCenter(bounds.getCenter());
            mapInstanceRef.current.setZoom(12);
        }
    };

    if (!isVisible) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                width: '90vw',
                height: '90vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
                {/* Header */}
                <div style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#fafafa',
                    borderRadius: '8px 8px 0 0'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '18px' }}>📍</span>
                        <h3 style={{ margin: 0 }}>Facilities Map - Eswatini</h3>
                        <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                            ({facilities?.filter(f => f.latitude && f.longitude).length || 0} facilities with coordinates)
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '20px',
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '4px',
                            color: '#666'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                        onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div style={{ flex: 1, position: 'relative', padding: '12px' }}>
                    {/* Legend */}
                    <div style={{ 
                        position: 'absolute', 
                        top: '20px', 
                        right: '20px', 
                        zIndex: 1000,
                        backgroundColor: 'white',
                        padding: '12px',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        minWidth: '200px',
                        maxWidth: '300px'
                    }}>
                        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>
                            Legend
                        </div>
                        
                        {/* Status Legend */}
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '500', marginBottom: '4px' }}>Status:</div>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <div style={{ 
                                        width: '12px', height: '12px', borderRadius: '50%', 
                                        backgroundColor: '#52c41a', border: '2px solid #fff' 
                                    }} />
                                    <span style={{ fontSize: '10px' }}>Active</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <div style={{ 
                                        width: '10px', height: '10px', borderRadius: '50%', 
                                        backgroundColor: '#ff4d4f', border: '2px solid #fff' 
                                    }} />
                                    <span style={{ fontSize: '10px' }}>Inactive</span>
                                </div>
                            </div>
                        </div>

                        {/* Region Legend */}
                        <div>
                            <div style={{ fontSize: '11px', fontWeight: '500', marginBottom: '4px' }}>Regions:</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {[
                                    { name: 'Hhohho', color: '#1890ff' },
                                    { name: 'Lubombo', color: '#52c41a' },
                                    { name: 'Manzini', color: '#fa8c16' },
                                    { name: 'Shiselweni', color: '#722ed1' }
                                ].map(region => (
                                    <div key={region.name} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <div style={{ 
                                            width: '12px', height: '12px', borderRadius: '50%', 
                                            backgroundColor: region.color, border: '2px solid #fff' 
                                        }} />
                                        <span style={{ fontSize: '10px' }}>{region.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Active Filters */}
                        {Object.keys(activeFilters || {}).length > 0 && (
                            <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                                <div style={{ fontSize: '11px', fontWeight: '500', marginBottom: '4px' }}>Active Filters:</div>
                                {activeFilters.level && (
                                    <span style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        fontSize: '11px',
                                        backgroundColor: '#1890ff',
                                        color: 'white',
                                        borderRadius: '12px',
                                        marginRight: '4px',
                                        marginBottom: '4px'
                                    }}>
                                        {activeFilters.level}
                                    </span>
                                )}
                                {activeFilters.region && (
                                    <span style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        fontSize: '11px',
                                        backgroundColor: '#52c41a',
                                        color: 'white',
                                        borderRadius: '12px',
                                        marginRight: '4px',
                                        marginBottom: '4px'
                                    }}>
                                        {activeFilters.region}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Map Container */}
                    <div 
                        ref={mapRef} 
                        style={{ 
                            width: '100%', 
                            height: '100%',
                            borderRadius: '6px',
                            border: '1px solid #d9d9d9'
                        }} 
                    />

                    {/* No coordinates warning */}
                    {facilities && facilities.filter(f => f.latitude && f.longitude).length === 0 && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: 'rgba(255, 255, 255, 0.95)',
                            padding: '20px',
                            borderRadius: '8px',
                            textAlign: 'center',
                            border: '1px solid #d9d9d9',
                            zIndex: 1000
                        }}>
                            <div style={{ fontSize: '24px', marginBottom: '8px' }}>📍</div>
                            <div style={{ color: '#666' }}>No facilities with valid coordinates found</div>
                            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                                Add latitude and longitude to facilities to display them on the map
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
// Main Facilities Component
export default function Facilities() {
    const [result, setResult] = useState({ data: [], page: {} });
    const [originalResult, setOriginalResult] = useState({ data: [], page: {} }); // Store original unfiltered data
    const [allFacilities, setAllFacilities] = useState([]);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const [activeFilters, setActiveFilters] = useState({});
    const sdataRef = useRef({ p: 1, ps: 25 });
    const formRef = useRef();
    const configRef = useRef();
    const [stationPoints, setStationPoints] = useState([]);
    const [assetCats, setAssetCats] = useState([]);
    
    const [showMap, setShowMap] = useState(false);

    const list = (p, ps, forceNormalPagination = false) => {
        // When analytics is enabled, load all data on one page for better filtering
        if (showAnalytics && !forceNormalPagination) {
            // First get the total count
            util.showLoader();
            FacilityService.list({ p: 1, ps: 1, k: sdataRef.current.k }).then(({ data }) => {
                const totalRecords = data.result.page.total_records;
                
                if (totalRecords > 0) {
                    // Now fetch all records
                    const allDataParams = {
                        p: 1,
                        ps: totalRecords, // Load all records
                        k: sdataRef.current.k // Include search if any
                    };
                    
                    return FacilityService.list(allDataParams);
                } else {
                    return { data };
                }
            }).then(({ data }) => {
                setOriginalResult(data.result);
                setResult(data.result);
                setActiveFilters({});
            }).catch(e => {
                message.error(e.message);
            }).finally(() => {
                util.hideLoader();
            });
        } else {
            // Normal pagination mode
            sdataRef.current.p = p || 1;
            sdataRef.current.ps = ps || sdataRef.current.ps;
            
            util.showLoader();
            FacilityService.list(sdataRef.current).then(({ data }) => {
                setOriginalResult(data.result);
                setResult(data.result);
                setActiveFilters({});
            }).catch(e => {
                message.error(e.message);
            }).finally(() => {
                util.hideLoader();
            });
        }
    }

    // Apply client-side filtering to the displayed data
    const applyClientSideFilter = (filters) => {
        let filteredData = [...originalResult.data];
        
        // Apply level filter
        if (filters.level) {
            filteredData = filteredData.filter(facility => 
                (facility.site_type || 'Not Specified') === filters.level
            );
        }
        
        // Apply region filter
        if (filters.region) {
            filteredData = filteredData.filter(facility => 
                (facility.located_region || 'Not Specified') === filters.region
            );
        }
        
        // Update the result with filtered data while maintaining pagination structure
        const filteredResult = {
            ...originalResult,
            data: filteredData,
            page: {
                ...originalResult.page,
                total: filteredData.length,
                total_records: filteredData.length,
                start: 0,
                cur_page: 1
            }
        };
        
        setResult(filteredResult);
    };

    // Handle filter from analytics
    const handleAnalyticsFilter = (filterType, filterValue) => {
        const newFilters = { ...activeFilters };
        
        if (newFilters[filterType] === filterValue) {
            // If clicking the same filter, remove it
            delete newFilters[filterType];
        } else {
            // Apply new filter
            newFilters[filterType] = filterValue;
        }
        
        setActiveFilters(newFilters);
        
        // Apply client-side filtering
        if (Object.keys(newFilters).length === 0) {
            // No filters, show original data
            setResult(originalResult);
        } else {
            // Apply filters
            applyClientSideFilter(newFilters);
        }
    };

    // Clear all filters
    const clearAllFilters = () => {
        setActiveFilters({});
        setResult(originalResult); // Reset to original data
    };

    // Fetch all facilities for analytics
    const fetchAllFacilities = async () => {
        if (!showAnalytics) return;
        
        setLoadingAnalytics(true);
        try {
            // Get total count first
            const response = await FacilityService.list({ p: 1, ps: 1 });
            const totalRecords = response.data.result.page.total_records;
            
            if (totalRecords > 0) {
                // Fetch all records in one go
                const allDataResponse = await FacilityService.list({ 
                    p: 1, 
                    ps: totalRecords,
                    // Include any search filters from current view
                    k: sdataRef.current.k 
                });
                setAllFacilities(allDataResponse.data.result.data || []);
            } else {
                setAllFacilities([]);
            }
        } catch (error) {
            console.error('Error fetching all facilities for analytics:', error);
            message.error('Failed to load analytics data');
            setAllFacilities([]);
        } finally {
            setLoadingAnalytics(false);
        }
    }

    const deleteRecord = (id) => {
        message.destroy();
        confirm({
            title: 'Do you Want to delete this facility?',
            icon: <ExclamationCircleOutlined />,
            content: '',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                util.showLoader();
                FacilityService.delete(id).then(({ data }) => {
                    message.success(data.message || 'Deleted');
                    list();
                    // Refresh analytics data after deletion
                    if (showAnalytics) {
                        fetchAllFacilities();
                    }
                }).catch(e => {
                    message.error(e.message);
                }).finally(() => {
                    util.hideLoader();
                })
            },
            onCancel() {
            },
        })
    }

    // Effect to reload data when analytics is toggled
    useEffect(() => {
        if (showAnalytics) {
            fetchAllFacilities();
            // Reload data to show all records when analytics is enabled
            list();
        } else {
            // When analytics is disabled, reload with normal pagination
            list(1, 25, true); // Force normal pagination
        }
        // Initial analytics load
    if (showAnalytics) {
        fetchAllFacilities();
    }

    // Load Google Maps API
    if (!window.google) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCb4XGt5HUm9-DWDp_CbM2kCsdvLSrkvCs&libraries=places`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }
    
    return () => { message.destroy() }
    
    }, [showAnalytics]);

    // Effect to refresh analytics when search changes
    useEffect(() => {
        if (showAnalytics) {
            const timeoutId = setTimeout(() => {
                fetchAllFacilities();
            }, 500); // Debounce search
            
            return () => clearTimeout(timeoutId);
        }
    }, [sdataRef.current?.k, showAnalytics]);

    // Effect to reapply filters when original data changes
    useEffect(() => {
        if (Object.keys(activeFilters).length > 0 && originalResult.data.length > 0) {
            applyClientSideFilter(activeFilters);
        }
    }, [originalResult]);

    useEffect(() => {
        list();
        FacilityService.allStationPoints({ status: 1 }).then(res => {
            setStationPoints(res.data.result.data.sort((a, b) => ('' + a.name).localeCompare(b.name)));
        })
        AssetService.allCats({ status: 1 }).then(res => setAssetCats(res.data.result.data))
        
        // Initial analytics load
        if (showAnalytics) {
            fetchAllFacilities();
        }
         if (!window.google) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCb4XGt5HUm9-DWDp_CbM2kCsdvLSrkvCs&libraries=places`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }
        return () => { message.destroy() }
    }, []);

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
    <div className="my-auto">
        <h2>Facilities</h2>
    </div>
    <div className="my-auto ml-auto">
        <div className="d-flex align-items-center">
            <span className="mr10" style={{ fontSize: '14px', color: '#666' }}>
                <BarChartOutlined /> Analytics {showAnalytics && '(All Data Mode)'}
            </span>
            <Switch 
                checked={showAnalytics} 
                onChange={setShowAnalytics}
                size="small"
            />
            {/* Add this new map button */}
            <Button 
                type="default" 
                icon={<EnvironmentOutlined />}
                onClick={() => setShowMap(true)}
                style={{ marginLeft: '12px' }}
            >
                View Map
            </Button>
        </div>
    </div>
</div>

            <div className="page-pad">
                {/* Analytics Panel */}
                <AnalyticsPanel 
                    facilities={allFacilities} 
                    isVisible={showAnalytics}
                    loading={loadingAnalytics}
                    totalRecords={result.page.total_records}
                    onFilterClick={handleAnalyticsFilter}
                    activeFilters={activeFilters}
                />

                {/* Active Filters Display */}
                {Object.keys(activeFilters).length > 0 && (
                    <div className="active-filters" style={{ 
                        marginBottom: '16px', 
                        padding: '12px 16px', 
                        background: '#e6f7ff', 
                        borderRadius: '6px',
                        border: '1px solid #91d5ff'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                            <span style={{ color: '#1890ff', fontWeight: '500' }}>Active Filters:</span>
                            {activeFilters.level && (
                                <Tag 
                                    closable 
                                    color="blue" 
                                    onClose={() => handleAnalyticsFilter('level', activeFilters.level)}
                                >
                                    Level: {activeFilters.level}
                                </Tag>
                            )}
                            {activeFilters.region && (
                                <Tag 
                                    closable 
                                    color="green" 
                                    onClose={() => handleAnalyticsFilter('region', activeFilters.region)}
                                >
                                    Region: {activeFilters.region}
                                </Tag>
                            )}
                            <Button 
                                size="small" 
                                type="link" 
                                onClick={clearAllFilters}
                                style={{ padding: '0 4px', height: 'auto' }}
                            >
                                Clear All
                            </Button>
                        </div>
                    </div>
                )}

                {result.data.length > 0 &&
                    <div className="text-secondary mb8">
                        {showAnalytics ? (
                            <>
                                Showing {result.data.length} facilities
                                {Object.keys(activeFilters).length > 0 
                                    ? ` (filtered from ${originalResult.data.length} total)` 
                                    : ` (all data loaded)`
                                }
                                {allFacilities.length > 0 && (
                                    <span className="ml10" style={{ color: '#1890ff', fontSize: '12px' }}>
                                        - Analytics mode active
                                    </span>
                                )}
                            </>
                        ) : (
                            <>
                                Showing {result.page.start + 1} - {result.page.start + result.page.total} of {result.page.total_records} records.
                            </>
                        )}
                    </div>
                }

                <div className="d-flex tbl-search-head">
                    <div className="my-auto">
                        <SearchForm dataRef={sdataRef} onSearch={list} />
                    </div>
                    <div className="ml-auto my-auto">
                        <Button type="primary" onClick={() => formRef.current.openForm()}>
                            <img src={plus} alt='' className='mr5' height={15}/>  Add
                        </Button>
                    </div>
                </div>

                <If cond={result.data.length}>
                    <div className="table-responsive">
                        <table className="table table-bordered table-md table-striped table-hover m-0">
                            <thead className="thead-light text-uppercase table-text-vmid">
                                <tr>
                                    <th className="w20">SN</th>
                                    <th>Facility/Site</th>
                                    <th className="w300">Asset Category</th>
                                    <th className="w150">Admin Name</th>
                                    <th className="w60">Status</th>
                                    <th className="w60"></th>
                                    <th className="w70"></th>
                                </tr>
                            </thead>
                            <tbody className="table-text-top">
                                {result.data.map((v, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}.</td>
                                        <td>
                                            {v.business_name}
                                            <div className="note-text pt3">
                                                <div className="d-flex">
                                                    <div className="w80">RC</div>
                                                    <div className="bold600 pl5">: {v.site_code}</div>
                                                </div>
                                                <div className="d-flex">
                                                    <div className="w80">Facility Code</div>
                                                    <div className="bold600 pl5">: {v.fac_code}</div>
                                                </div>
                                                <div className="d-flex">
                                                    <div className="w80">Facility Owner</div>
                                                    <div className="bold600 pl5">: {v.facility_owner || 'N/A'}</div>
                                                </div>
                                                <div className="d-flex">
                                                    <div className="w80">Level of Health Care</div>
                                                    <div className="bold600 pl5">: {v.site_type || 'N/A'}</div>
                                                </div>
                                                <div className="d-flex">
                                                    <div className="w80">Region</div>
                                                    <div className="bold600 pl5">: {v.located_region || 'N/A'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            {assetCats.map((r) => {
                                                if (v.asset_cat_ids?.split(',').includes(r.id)) {
                                                    let color = "magenta";
                                                    color = r.name === 'Medical' ? 'green' : (r.name === 'Non Medical' ? 'purple' : 'gold')
                                                    return <Tag key={r.id} color={color}>{r.name}</Tag>
                                                } else {
                                                    return null;
                                                }
                                            })}
                                        </td>
                                        <td>
                                            {v.name}
                                            <div className="note-text">({v.email})</div>
                                        </td>
                                        <td className="nowrap">
                                            {v.status ? (<AntdTag type="success">Active</AntdTag>) : (<AntdTag type="danger">Inactive</AntdTag>)}
                                        </td>
                                        <td className="text-center">
Click to add more to this facility
<button type="button" className="btn btn-sm btn-primary nowrap" onClick={() => configRef.current.openForm(v.id, v.business_name)}>
    Station Points <span className="badge badge-warning">{v.station_points_count}</span>
</button>
Click to Copy to others on the same level:
<button type="button" className="btn btn-sm btn-primary nowrap" onClick={() => configRef.current.openForm(v.id, v.business_name, v.site_type)}>
    Station Points <span className="badge badge-warning">{v.station_points_count}</span>
</button>
                                        </td>
                                        <td className="text-center">
                                            <Button.Group size="small">
                                                <Button type="default" onClick={() => formRef.current.openForm(v)}>
                                                <img src={edit} alt='' height={15}/> 
                                                </Button>
                                                <Button type="default" onClick={() => deleteRecord(v.id)} disabled={util.getUserType() !== 'ADMIN' && v.is_national * 1}>
                                                <img src={xmark} alt='' className='mr5' height={15}/> 
                                                </Button>
                                            </Button.Group>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Show pagination only when analytics is disabled */}
                    {!showAnalytics && (
                        <div className="d-flex tbl-foot-bx">
                            <AntdPaging
                                onChange={list}
                                total={result.page.total_records}
                                current={result.page.cur_page}
                                pageSize={sdataRef.current.ps}
                                showSizeChanger
                            />
                        </div>
                    )}
                    {/* Show analytics mode info when analytics is enabled */}
                    {showAnalytics && (
                        <div className="d-flex tbl-foot-bx">
                            <div style={{ 
                                padding: '16px', 
                                textAlign: 'center', 
                                color: '#666',
                                fontStyle: 'italic',
                                width: '100%'
                            }}>
                                {Object.keys(activeFilters).length > 0 
                                    ? `Showing ${result.data.length} filtered facilities`
                                    : `All ${result.data.length} facilities loaded (Analytics Mode)`
                                }
                                <span style={{ marginLeft: '10px', color: '#1890ff' }}>
                                    • Click analytics cards above to filter
                                </span>
                            </div>
                        </div>
                    )}
                </If>
                <If cond={!result.data.length}>
                    <div className="no-rec">No record found</div>
                </If>
            </div>

            <AddForm
                ref={formRef}
                callback={(pageNum) => {
                    list(pageNum);
                    // Refresh analytics after add/edit
                    if (showAnalytics) {
                        fetchAllFacilities();
                    }
                }}
                pageno={1} // Always use page 1 since we're loading all data in analytics mode
                assetCats={assetCats}
            />
            <ConfigForm
                ref={configRef}
                callback={(pageNum) => {
                    list(pageNum);
                    // Refresh analytics after config change
                    if (showAnalytics) {
                        fetchAllFacilities();
                    }
                }}
                pageno={1} // Always use page 1 since we're loading all data in analytics mode
                stationPoints={stationPoints}
            />
    <FacilitiesMap
    facilities={showAnalytics ? allFacilities : result.data}
    isVisible={showMap}
    onClose={() => setShowMap(false)}
    activeFilters={activeFilters}
/>
        </div>
    )
}

// Search Form Component
const SearchForm = (props) => {
    let { dataRef, onSearch } = props;
    let [data, setData] = useState({ ...dataRef.current });
    const handleChange = (v, k) => {
        data[k] = v;
        setData({ ...data });
    }
    useEffect(() => {
        dataRef.current = { ...data };
    }, [data]);

    useEffect(() => {
        setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps });
    }, [dataRef.current.p, dataRef.current.ps]);

    return (
        <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
            <div className="d-flex">
                <div>
                    <Input placeholder="Search" allowClear value={data.k} onChange={e => handleChange(e.target.value, 'k')} />
                </div>
                <div>
                    <Button type="primary" icon={<img src={search} alt='' height={15}/> } onClick={() => onSearch()}></Button>
                </div>
            </div>
        </form>
    )
}

// Add/Edit Form Component
const AddForm = forwardRef((props, ref) => {
    let { callback, pageno, assetCats } = props;
    const [showForm, setShowForm] = useState(false);
    let [data, setData] = useState({});
    const handleChange = (v, k) => {
        data[k] = v;
        setData({ ...data });
    }
    const save = () => {
        message.destroy();
        util.showLoader();
        FacilityService.save(data).then(({ data }) => {
            message.success(data.message || 'Saved');
            callback(data.id ? pageno : 1);
            setShowForm(false);
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })
    }
    const closeForm = () => {
        setShowForm(false);
    }

    useImperativeHandle(ref, () => ({
        openForm(dtl) {
            setData(dtl ? { ...dtl } : { is_national: '0', status: '1' });
            setShowForm(true);
        }
    }));

    return (
        <Modal
            title={`${data.id ? 'Edit' : 'Add'} Facility`}
            visible={showForm}
            okText="Save"
            onOk={save}
            onCancel={closeForm}
            destroyOnClose
            maskClosable={false}
            width={1000}
        >
            <form onSubmit={e => { e.preventDefault(); save() }} autoComplete="off" spellCheck="false">
                {util.getUserType() === 'ADMIN' &&
                    <div className="row mingap">
                        <div className="col-md-3 form-group">
                            <label>Is National?</label>
                            <div>
                                <AntdSelect
                                    options={[{ value: '0', label: "No" }, { value: '1', label: "Yes" }]}
                                    value={data.is_national}
                                    onChange={v => { handleChange(v, 'is_national') }}
                                />
                            </div>
                        </div>
                    </div>
                }

                <div className="mb15">
                    <Card size="small" type="inner" title="Facility Detail">
                        <div className="">
                            <div className="row mingap">
                                <div className="col-md-3 form-group">
                                    <label className="req">RC</label>
                                    <Input value={data.site_code || ''} onChange={e => handleChange(e.target.value, 'site_code')} />
                                </div>
                                <div className="col-md-3 form-group">
                                    <label className="req">Facility Code</label>
                                    <Input value={data.fac_code || ''} onChange={e => handleChange(e.target.value, 'fac_code')} />
                                </div>
                                <div className="col-md-6 form-group">
                                    <label className="req">Facility Name</label>
                                    <Input value={data.business_name || ''} onChange={e => handleChange(e.target.value, 'business_name')} />
                                </div>
                                <div className="col-md-3 form-group">
                                    <label className="">Facility Owner</label>
                                    <Input value={data.facility_owner || ''} onChange={e => handleChange(e.target.value, 'facility_owner')} />
                                </div>
                                <div className="col-md-3 form-group">
                                    <label className="">Facility Level</label>
                                   <div>
                                        <AntdSelect
                                            options={["Level 1", "Level 2", "Level 3", "Level 4", "Level 5"]}
                                            value={data.site_type}
                                            onChange={v => { handleChange(v, 'site_type') }}
                                        />
                                    </div>  
                                </div>
                                <div className="col-md-3 form-group">
                                    <label className="req">Facility Tel</label>
                                    <Input value={data.phone1 || ''} onChange={e => handleChange(e.target.value, 'phone1')} />
                                </div>
                                
                                <div className="col-md-3 form-group">
                                    <label className="">Region</label>
                                   <div>
                                        <AntdSelect
                                            options={["Hhohho", "Lubombo", "Manzini", "Shiselweni"]}
                                            value={data.located_region}
                                            onChange={v => { handleChange(v, 'located_region') }}
                                        />
                                    </div>  
                                </div>

                                <div className="col-md-12 form-group">
                                    <label className="req">Facility Address</label>
                                    <Input value={data.address || ''} onChange={e => handleChange(e.target.value, 'address')} />
                                </div>

                                <div className="col-md-3 form-group">
                                    <label className="">City</label>
                                    <Input value={data.city || ''} onChange={e => handleChange(e.target.value, 'city')} />
                                </div>
                                <div className="col-md-3 form-group">
                                    <label className="">Pincode</label>
                                    <Input value={data.pincode || ''} onChange={e => handleChange(e.target.value, 'pincode')} />
                                </div>

                                <div className="col-md-3 form-group">
                                    <label className="">Latitude</label>
                                    <Input type="number" value={data.latitude || ''} onChange={e => handleChange(e.target.value, 'latitude')} />
                                </div>
                                <div className="col-md-3 form-group">
                                    <label className="">Longitude</label>
                                    <Input type="number" value={data.longitude || ''} onChange={e => handleChange(e.target.value, 'longitude')} />
                                </div>
                            </div>

                            <div>
                                <label className="req">Asset Category</label>
                                <div className="form-control">
                                    <MultiChechBox value={data.asset_cat_ids} options={assetCats} onChange={v => handleChange(v, 'asset_cat_ids')} />
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="mb15">
                    <Card size="small" type="inner" title="Admin User Detail">
                        <div className="row mingap">
                            <div className="col-md-4 form-group">
                                <label className="req">Name</label>
                                <Input value={data.name || ''} onChange={e => handleChange(e.target.value, 'name')} />
                            </div>
                            <div className="col-md-4 form-group">
                                <label className="req">Email</label>
                                <Input value={data.email || ''} onChange={e => handleChange(e.target.value, 'email')} />
                            </div>
                            <div className="col-md-4 form-group">
                                <label className="req">Mobile</label>
                                <PositiveNumInput className="form-control" value={data.mobile || ''} onChange={v => handleChange(v, 'mobile')} type="int" maxLength="10" />
                            </div>
                            <div className="col-md-4 form-group">
                                <label className="req">
                                    Username
                                </label>
                                <Input value={data.username || ''} onChange={e => handleChange(e.target.value, 'username')} />
                            </div>
                            <div className="col-md-4 form-group">
                                <label className="">Password</label>
                                <Input value={data.password || ''} onChange={e => handleChange(e.target.value, 'password')} />
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="row mingap">
                    <div className="col-md-3 form-group">
                        <label className="req">Status</label>
                        <div>
                            <AntdSelect
                                options={[{ value: '1', label: "Active" }, { value: '0', label: "Inactive" }]}
                                value={data.status}
                                onChange={v => { handleChange(v, 'status') }}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    )
})

// Enhanced Config Form Component for Station Points with Copy Feature
const ConfigForm = forwardRef((props, ref) => {
    let { stationPoints, callback, pageno } = props;
    const [clientDtl, setClientDtl] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [data, setData] = useState([]);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [facilitiesOfSameLevel, setFacilitiesOfSameLevel] = useState([]);
    const [selectedFacilitiesToCopy, setSelectedFacilitiesToCopy] = useState([]);
    const [loadingFacilities, setLoadingFacilities] = useState(false);

    const save = () => {
        message.destroy();
        util.showLoader();
        FacilityService.saveConfig({ clientId: clientDtl.id, data: data.join() }).then(({ data }) => {
            message.success(data.message || 'Saved');
            setShowForm(false);
            callback(data.id ? pageno : 1);
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })
    }

    const closeForm = () => {
        setShowForm(false);
    }

    // New function to fetch facilities of the same level
    const fetchFacilitiesOfSameLevel = async () => {
        if (!clientDtl.site_type) {
            message.warning('Current facility must have a level/site_type to copy station points');
            return;
        }

        setLoadingFacilities(true);
        try {
            // Option 1: If backend supports site_type filtering (recommended)
            // const response = await FacilityService.all({ 
            //     status: 1, 
            //     site_type: clientDtl.site_type 
            // });
            // const facilities = response.data.result.data.filter(facility => 
            //     facility.id !== clientDtl.id
            // );

            // Option 2: Get all facilities and filter on frontend (current implementation)
            const response = await FacilityService.all({ status: 1 });
            const facilities = response.data.result.data.filter(facility => 
                facility.id !== clientDtl.id && 
                facility.site_type === clientDtl.site_type
            );
            
            setFacilitiesOfSameLevel(facilities);
            setShowCopyModal(true);
        } catch (error) {
            message.error('Failed to fetch facilities of the same level');
            console.error(error);
        } finally {
            setLoadingFacilities(false);
        }
    }

    // New function to copy station points to selected facilities
    const copyStationPointsToFacilities = async () => {
        if (selectedFacilitiesToCopy.length === 0) {
            message.warning('Please select at least one facility to copy station points to');
            return;
        }

        if (data.length === 0) {
            message.warning('No station points selected to copy');
            return;
        }

        try {
            util.showLoader();
            
            // Copy station points to each selected facility
            const copyPromises = selectedFacilitiesToCopy.map(facilityId => 
                FacilityService.saveConfig({ 
                    clientId: facilityId, 
                    data: data.join() 
                })
            );

            await Promise.all(copyPromises);
            
            message.success(`Station points copied to ${selectedFacilitiesToCopy.length} facilities successfully`);
            setShowCopyModal(false);
            setSelectedFacilitiesToCopy([]);
            callback(pageno);
        } catch (error) {
            message.error('Failed to copy station points to some facilities');
            console.error(error);
        } finally {
            util.hideLoader();
        }
    }

    useImperativeHandle(ref, () => ({
        openForm(id, name, site_type) {
            FacilityService.allStationPoints({ client_id: id }).then(res => {
                setData(res.data.result.data.map((v) => v.id));
            });
            setShowForm(true);
            setClientDtl({ id, name, site_type });
        }
    }));

    return (
        <>
            <Modal
                title={
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            Station Points - <span className="text-secondary">{clientDtl.name}</span>
                            {clientDtl.site_type && (
                                <span className="badge badge-info ml-2">{clientDtl.site_type}</span>
                            )}
                        </div>
                        <Button 
                            type="default" 
                            size="small"
                            onClick={fetchFacilitiesOfSameLevel}
                            loading={loadingFacilities}
                            disabled={!clientDtl.site_type || data.length === 0}
                        >
                            Copy to Same Level Facilities
                        </Button>
                    </div>
                }
                open={showForm}
                okText="Save"
                onOk={save}
                onCancel={closeForm}
                destroyOnClose
                maskClosable={false}
                width={1200}
                style={{ top: 20 }}
                bodyStyle={{ height: 'calc(100vh - 150px)' }}
            >
                <form onSubmit={e => { e.preventDefault(); save() }} autoComplete="off" spellCheck="false">
                    {data.length > 0 && clientDtl.site_type && (
                        <div className="alert alert-info mb-3">
                            <small>
                                <strong>Tip:</strong> After saving, you can copy these {data.length} selected station points 
                                to other facilities with the same level ({clientDtl.site_type}) using the "Copy to Same Level Facilities" button.
                            </small>
                        </div>
                    )}
                    
                    <div className="form-group noselect">
                        <div className="row mingap">
                            {stationPoints.map((v, i) => (
                                <div key={i} className="col-md-3 mt5">
                                    <div className="form-control">
                                        <Checkbox
                                            checked={data.includes(v.id)}
                                            onChange={e => {
                                                let dt = [...data];
                                                if (e.target.checked) {
                                                    dt.push(v.id);
                                                } else {
                                                    dt.splice(dt.indexOf(v.id), 1);
                                                }
                                                setData(dt);
                                            }}
                                        >
                                            {v.name}
                                        </Checkbox>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </form>
            </Modal>

            {/* Copy Station Points Modal */}
            <Modal
                title={`Copy Station Points to Other ${clientDtl.site_type} Facilities`}
                open={showCopyModal}
                onCancel={() => {
                    setShowCopyModal(false);
                    setSelectedFacilitiesToCopy([]);
                }}
                onOk={copyStationPointsToFacilities}
                okText={`Copy to ${selectedFacilitiesToCopy.length} Selected Facilities`}
                okButtonProps={{ disabled: selectedFacilitiesToCopy.length === 0 }}
                width={800}
                destroyOnClose
            >
                <div className="mb-3">
                    <div className="alert alert-warning">
                        <strong>Warning:</strong> This will replace all existing station points for the selected facilities 
                        with the {data.length} station points currently selected for "{clientDtl.name}".
                    </div>
                </div>

                {facilitiesOfSameLevel.length === 0 ? (
                    <div className="text-center p-4">
                        <div className="text-muted">
                            No other facilities found with level "{clientDtl.site_type}"
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <span>
                                    Select facilities to copy station points to ({facilitiesOfSameLevel.length} available):
                                </span>
                                <div>
                                    <Button 
                                        size="small" 
                                        type="link"
                                        onClick={() => setSelectedFacilitiesToCopy(facilitiesOfSameLevel.map(f => f.id))}
                                    >
                                        Select All
                                    </Button>
                                    <Button 
                                        size="small" 
                                        type="link"
                                        onClick={() => setSelectedFacilitiesToCopy([])}
                                    >
                                        Clear All
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <div className="row">
                                {facilitiesOfSameLevel.map((facility) => (
                                    <div key={facility.id} className="col-md-6 mb-2">
                                        <div className="card" style={{ 
                                            border: selectedFacilitiesToCopy.includes(facility.id) 
                                                ? '2px solid #1890ff' 
                                                : '1px solid #d9d9d9',
                                            backgroundColor: selectedFacilitiesToCopy.includes(facility.id) 
                                                ? '#f6ffed' 
                                                : 'white'
                                        }}>
                                            <div className="card-body p-3">
                                                <div className="form-check">
                                                    <Checkbox
                                                        checked={selectedFacilitiesToCopy.includes(facility.id)}
                                                        onChange={(e) => {
                                                            const facilityId = facility.id;
                                                            if (e.target.checked) {
                                                                setSelectedFacilitiesToCopy(prev => [...prev, facilityId]);
                                                            } else {
                                                                setSelectedFacilitiesToCopy(prev => 
                                                                    prev.filter(id => id !== facilityId)
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <div>
                                                            <strong>{facility.business_name}</strong>
                                                            <div className="small text-muted">
                                                                RC: {facility.site_code} | 
                                                                Code: {facility.fac_code} | 
                                                                Region: {facility.located_region || 'N/A'}
                                                            </div>
                                                            <div className="small text-muted">
                                                                Current Station Points: {facility.station_points_count || 0}
                                                            </div>
                                                        </div>
                                                    </Checkbox>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {selectedFacilitiesToCopy.length > 0 && (
                            <div className="mt-3 p-3 bg-light border rounded">
                                <strong>Selected for copying:</strong> {selectedFacilitiesToCopy.length} facilities will receive {data.length} station points
                            </div>
                        )}
                    </>
                )}
            </Modal>
        </>
    )
})